import React, { useState } from 'react';
import classNames from 'classnames';


const APP_URL = 'https://flashcards.codescribes.io';

// Counted from the live catalog at launch. Rounded down on purpose so the page
// stays true as sets are added, rather than going stale the first time one is.
const STATS = [
  { value: '600+', label: 'Flashcards' },
  { value: '18', label: 'Card sets' },
  { value: '3', label: 'Exams covered' },
  { value: '$0', label: 'Free during launch' }
];

// Spelled out rather than implied: not every reader has been burned by a prep
// course yet, so the page has to name the problem before it can sell the fix.
const PROBLEM = {
  heading: 'Prep material teaches you the test, not the technology',
  points: [
    "Prep courses often don't reflect what's actually on the exam, so you can finish one and still be surprised on exam day.",
    'Practice tests lean on memorizing case studies. You learn to recognize a scenario and its answer, not why the answer is right.',
    'Change the wording of the question and that memorized answer stops working.'
  ]
};

const FIX = {
  heading: 'Scribe Cards teach the concepts the exam is built on',
  points: [
    'Each card comes from the study notes of an engineer who took the exam and passed it, so the cards cover what the exam really asks about.',
    'Each card tests a term or concept, not a scenario, so you can still answer when the question is worded differently.',
    'Every answer explains why each option is right or wrong, so you build understanding instead of memorizing answers.'
  ]
};

// A real card from the MCP set, so the demo is exactly what you'd get in the
// app rather than a marketing approximation of it.
const SAMPLE_CARD = {
  deck: 'Model Context Protocol',
  course: 'CCAR-F',
  prompt: 'What problem does the Model Context Protocol solve?',
  options: [
    {
      id: 'a',
      text: 'It is a communication layer letting a Model access context and tools without needing custom code',
      correct: true,
      rationale: 'One protocol replaces a bespoke integration per service.'
    },
    {
      id: 'b',
      text: 'It is a hosting platform for running agents close to their data',
      correct: false,
      rationale: 'MCP is a protocol, not a runtime. Managed agent hosting is a different concern.'
    },
    {
      id: 'c',
      text: 'It is a caching layer that reduces the tokens a Model spends on tool definitions',
      correct: false,
      rationale: 'Prompt caching addresses token cost; MCP addresses integration.'
    },
    {
      id: 'd',
      text: 'It is an authentication standard for issuing API keys to Models',
      correct: false,
      rationale: 'Auth is something an MCP client handles, not the purpose of the protocol.'
    }
  ],
  explanation: 'MCP is a communication layer that lets a Model reach context and tools without custom integration code. Service providers like GitHub and Dropbox publish official servers, which is why the guidance is to look for one before writing your own.',
  citation: 'notes/anthropic/intro_to_mcp.md'
};

const COURSES = [
  {
    code: 'CCAR-F',
    name: 'Claude Certified Architect - Foundations',
    issuer: 'Anthropic',
    tagline: 'The Claude API, tool use, MCP, agent design, and multi-agent orchestration.',
    description: "Anthropic's foundational architect certification. The notes follow the exam from the first API call to a production agent, covering where each building block fits and why you'd reach for it.",
    topics: [
      'The Claude API end to end: streaming, tool use, structured output, RAG, and prompt caching',
      'The Model Context Protocol: its three primitives and who controls each one',
      'The Agents SDK, the ReAct loop, hooks, and what a subagent does and does not inherit',
      'Multi-agent patterns, Agent Skills, and Claude in GitHub'
    ],
    art: 'orchestra'
  },
  {
    code: 'AIF-C01',
    name: 'AWS Certified AI Practitioner',
    issuer: 'AWS',
    tagline: 'Core AI and ML concepts, evaluation metrics, Bedrock, SageMaker AI, and responsible AI.',
    description: "AWS's foundational AI and machine learning certification. The concepts come first, then the managed services, and the cards keep asking which one fits the job.",
    topics: [
      'Foundation versus frontier models, the three learning paradigms, and RLHF',
      'Evaluation metrics for classification, regression, and generative models',
      "Amazon's foundation models, Bedrock end to end, and SageMaker AI across the ML lifecycle",
      'Choosing between the managed AI services, and responsible AI'
    ],
    art: 'network'
  },
  {
    code: 'CLF-C02',
    name: 'AWS Certified Cloud Practitioner',
    issuer: 'AWS',
    tagline: 'The terminology, and what each AWS service is for.',
    description: 'The foundational AWS certification. The cards make sure you can name the right service for the job and explain why the alternatives fall short.',
    topics: [
      'Core compute, container, storage, and database services',
      'What makes a subnet public, private connectivity options, load balancers, Route 53, and the edge',
      'Identity, the security and governance suite, billing, and cost',
      'The analytics, AI, media, and IoT services'
    ],
    art: 'cloud'
  }
];

const WORKBENCH = [
  {
    code: 'SAA-C03',
    name: 'AWS Solutions Architect - Associate',
    blurb: 'Designing on AWS at associate depth: compute, networking, security, and the trade-offs between them.',
    status: 'Coming soon'
  },
  {
    code: 'AIP-C01',
    name: 'AWS Generative AI Developer',
    blurb: 'Building generative AI applications on AWS, from Bedrock to production. Notes and cards are being written now.',
    status: 'Coming soon'
  },
  {
    code: 'TRACK',
    name: 'DevOps Study Track',
    blurb: 'GitHub teams, CODEOWNERS and Actions, plus Ansible inventories, playbooks, and roles. No exam, just fundamentals.',
    status: 'Live'
  },
  {
    code: 'TRACK',
    name: 'AWS Marketplace',
    blurb: 'Listing types, entitlement versus metering, private offers, and the Marketplace API surface.',
    status: 'Live'
  }
];

const STEPS = [
  {
    title: 'Pick a course',
    body: 'Each course is a study guide: the topics one exam covers, the notes to read for them, and the cards that test them.'
  },
  {
    title: 'Read the notes',
    body: 'One topic per page, in reading order, hand-written by someone who holds the certification.'
  },
  {
    title: 'Practice, then review',
    body: 'Run a practice session, then review mode brings back everything you missed or starred until it sticks.'
  }
];


// Lucide paths, inlined so icons stay outline-only and inherit text color --
// the solid FontAwesome set is too heavy for the style guide's icon rules.
const ICON_PATHS = {
  arrowRight: 'M5 12h14M12 5l7 7-7 7',
  external: 'M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6',
  check: 'M20 6 9 17l-5-5',
  x: 'M18 6 6 18M6 6l12 12',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z',
  reset: 'M3 12a9 9 0 1 0 2.6-6.4M3 4v5h5'};

const Icon = ({ name, className = 'h-4 w-4' }) => {
  return (
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className={className}>
      <path d={ICON_PATHS[name]} />
    </svg>
  );
};


const PrimaryLink = ({ href, children }) => {
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      className='inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 font-body text-sm tracking-wide text-white shadow-sm transition-colors duration-150 hover:bg-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950'
    >
      {children}
      <Icon name='external' />
    </a>
  );
};

const OutlineLink = ({ href, children }) => {
  return (
    <a
      href={href}
      className='inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-neutral-700 px-5 py-2.5 font-body text-sm tracking-wide text-primary-light transition-colors duration-150 hover:border-neutral-500 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950'
    >
      {children}
    </a>
  );
};


const Overline = ({ children, className }) => {
  return (
    <p className={classNames('font-body text-xs uppercase tracking-widest text-primary-light', className)}>{children}</p>
  );
};

// Numbered heading with a hairline that runs to the edge, so the page reads
// like a bound notebook: chapter, title, rule.
const SectionHead = ({ num, title, blurb }) => {
  return (
    <header className='mb-12'>
      <div className='flex items-baseline gap-4'>
        <span className='font-mono text-xs tracking-[0.18em] text-primary-light'>{num}</span>
        <h2 className='font-heading text-4xl tracking-tight text-neutral-50 sm:text-5xl'>{title}</h2>
        <span aria-hidden='true' className='h-px flex-1 translate-y-[-0.4em] bg-neutral-800' />
      </div>
      {blurb ? <p className='mt-4 max-w-2xl font-body text-lg text-neutral-400'>{blurb}</p> : null}
    </header>
  );
};

// A teal rule broken by a diamond, the quill's nib in miniature.
const NibRule = () => {
  return (
    <div aria-hidden='true' className='flex items-center gap-3'>
      <span className='h-px w-16 bg-primary-light/60' />
      <span className='h-2 w-2 rotate-45 border border-primary-light' />
      <span className='h-px w-6 bg-primary-light/30' />
    </div>
  );
};

const DiamondList = ({ items }) => {
  return (
    <ul className='grid gap-3'>
      {items.map((item) => (
        <li key={item} className='relative pl-6 font-body text-base leading-normal text-neutral-400'>
          <span aria-hidden='true' className='absolute left-0 top-[0.62em] h-1.5 w-1.5 rotate-45 bg-primary-light' />
          {item}
        </li>
      ))}
    </ul>
  );
};


// The panel artwork sits in, with registration marks on opposite corners --
// the same framing the motifs get, so illustrations read as one set.
const ArtFrame = ({ children, className }) => {
  return (
    <div className={classNames(
      'group/frame relative rounded-lg border border-neutral-800 bg-neutral-900 bg-[radial-gradient(circle_at_50%_40%,rgba(43,176,191,0.10),transparent_70%)] p-6 transition-colors duration-150 hover:border-primary-light/40',
      className
    )}>
      <span aria-hidden='true' className='absolute -left-px -top-px h-3.5 w-3.5 rounded-tl-lg border-l-2 border-t-2 border-primary-light/60' />
      <span aria-hidden='true' className='absolute -bottom-px -right-px h-3.5 w-3.5 rounded-br-lg border-b-2 border-r-2 border-primary-light/60' />
      {children}
    </div>
  );
};


// Line-art motifs, one per course. Single-color teal line art per the style
// guide, with currentColor for the quiet strokes so they sit on the neutral
// scale.
const MOTIFS = {
  // An orchestrator fanning work out to subagents, each wired to its tools.
  orchestra: (
    <svg viewBox='0 0 400 300' role='img' aria-label='An orchestrator agent connected to three subagents, each with its own tools' className='h-auto w-full text-neutral-600'>
      <g stroke='currentColor' strokeWidth='1' opacity='0.5' strokeDasharray='3 6'>
        <line x1='200' y1='80' x2='90' y2='170' />
        <line x1='200' y1='80' x2='200' y2='170' />
        <line x1='200' y1='80' x2='310' y2='170' />
      </g>
      <g fill='none' stroke='currentColor' strokeWidth='1.5'>
        <line x1='90' y1='190' x2='60' y2='250' />
        <line x1='90' y1='190' x2='120' y2='250' />
        <line x1='200' y1='190' x2='200' y2='250' />
        <line x1='310' y1='190' x2='280' y2='250' />
        <line x1='310' y1='190' x2='340' y2='250' />
        <rect x='50' y='250' width='20' height='20' rx='3' />
        <rect x='110' y='250' width='20' height='20' rx='3' />
        <rect x='190' y='250' width='20' height='20' rx='3' />
        <rect x='270' y='250' width='20' height='20' rx='3' />
        <rect x='330' y='250' width='20' height='20' rx='3' />
      </g>
      <g fill='none' stroke='#2BB0BF' strokeWidth='2'>
        <circle cx='90' cy='180' r='12' />
        <circle cx='200' cy='180' r='12' />
        <circle cx='310' cy='180' r='12' />
      </g>
      <circle cx='200' cy='62' r='26' fill='none' stroke='#2BB0BF' strokeWidth='2.5' />
      <circle cx='200' cy='62' r='8' fill='#2BB0BF' />
      <g fill='#2BB0BF' opacity='0.6'>
        <circle cx='254' cy='36' r='3' />
        <circle cx='268' cy='54' r='2' />
        <circle cx='140' cy='40' r='2.5' />
      </g>
    </svg>
  ),
  // A small neural net: inputs, one hidden layer, a single output.
  network: (
    <svg viewBox='0 0 400 300' role='img' aria-label='A neural network with an input layer, a hidden layer, and one output' className='h-auto w-full text-neutral-600'>
      <g stroke='currentColor' strokeWidth='1' opacity='0.55'>
        {[70, 130, 190, 250].map((y1) => [60, 120, 180, 240].map((y2) => (
          <line key={`a${y1}-${y2}`} x1='80' y1={y1} x2='200' y2={y2} />
        )))}
      </g>
      <g stroke='#2BB0BF' strokeWidth='1.5' opacity='0.85'>
        {[60, 120, 180, 240].map((y) => (
          <line key={`b${y}`} x1='200' y1={y} x2='320' y2='150' />
        ))}
      </g>
      <g fill='#18181B' stroke='currentColor' strokeWidth='2'>
        {[70, 130, 190, 250].map((y) => <circle key={`i${y}`} cx='80' cy={y} r='11' />)}
      </g>
      <g fill='#18181B' stroke='#2BB0BF' strokeWidth='2'>
        {[60, 120, 180, 240].map((y) => <circle key={`h${y}`} cx='200' cy={y} r='11' />)}
      </g>
      <circle cx='320' cy='150' r='18' fill='none' stroke='#2BB0BF' strokeWidth='2.5' />
      <circle cx='320' cy='150' r='6' fill='#2BB0BF' />
    </svg>
  ),
  // A VPC boundary around a grid of services, with one request path lit
  // through it: edge, load balancer, compute, database.
  cloud: (
    <svg viewBox='0 0 400 300' role='img' aria-label='A grid of cloud services inside a network boundary, with one request path highlighted' className='h-auto w-full text-neutral-600'>
      <rect x='60' y='40' width='300' height='220' rx='10' fill='none' stroke='currentColor' strokeWidth='1.5' strokeDasharray='6 6' opacity='0.7' />
      <g fill='none' stroke='currentColor' strokeWidth='1.5'>
        {[90, 150, 210].map((y) => [100, 180, 260].map((x) => (
          <rect key={`s${x}-${y}`} x={x} y={y} width='40' height='40' rx='5' />
        )))}
      </g>
      <g fill='none' stroke='#2BB0BF' strokeWidth='2' strokeLinecap='round'>
        <path d='M20 110 H100' />
        <path d='M140 110 H180' />
        <path d='M200 130 V210' />
        <path d='M220 230 H260' />
      </g>
      <g fill='#18181B' stroke='#2BB0BF' strokeWidth='2'>
        <rect x='100' y='90' width='40' height='40' rx='5' />
        <rect x='180' y='90' width='40' height='40' rx='5' />
        <rect x='180' y='210' width='40' height='40' rx='5' />
        <rect x='260' y='210' width='40' height='40' rx='5' />
      </g>
      <circle cx='20' cy='110' r='6' fill='#2BB0BF' />
      <g fill='none' stroke='#2BB0BF' strokeWidth='1.5'>
        <ellipse cx='280' cy='224' rx='10' ry='4' />
        <path d='M270 224 v14 a10 4 0 0 0 20 0 v-14' />
      </g>
    </svg>
  )
};


// Three cards fanned out with the sample on top. Purely illustrative, so the
// whole thing is hidden from assistive tech and the real card further down
// carries the content.
const CardStack = () => {
  return (
    <ArtFrame className='mx-auto w-full max-w-md'>
      <div aria-hidden='true' className='relative mx-auto aspect-[4/3] w-full'>
        <div className='absolute inset-x-8 top-6 bottom-4 -rotate-6 rounded-lg border border-neutral-700 bg-neutral-800 transition-transform duration-500 group-hover/frame:-rotate-[9deg]' />
        <div className='absolute inset-x-6 top-4 bottom-4 rotate-3 rounded-lg border border-neutral-700 bg-neutral-800 transition-transform duration-500 group-hover/frame:rotate-6' />
        <div className='absolute inset-x-4 top-2 bottom-4 flex flex-col rounded-lg border border-primary-light/50 bg-neutral-900 p-5 shadow-lg transition-transform duration-500 group-hover/frame:-translate-y-1'>
          <div className='mb-3 flex items-center justify-between'>
            <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-primary-light'>{SAMPLE_CARD.course}</span>
            <span className='font-mono text-[10px] text-neutral-500'>12 / 22</span>
          </div>
          <p className='mb-4 font-heading text-lg leading-snug text-neutral-50'>{SAMPLE_CARD.prompt}</p>
          <div className='mt-auto grid gap-2'>
            {SAMPLE_CARD.options.slice(0, 3).map((option) => (
              <div key={option.id} className={classNames(
                'flex items-center gap-2 rounded-md border px-2.5 py-1.5',
                option.correct ? 'border-success/40 bg-[rgba(74,222,128,0.10)]' : 'border-neutral-800'
              )}>
                <span className={classNames('font-mono text-[10px]', option.correct ? 'text-[#4ADE80]' : 'text-neutral-500')}>{option.id.toUpperCase()}</span>
                <span className={classNames('h-1.5 flex-1 rounded-sm', option.correct ? 'bg-[#4ADE80]/40' : 'bg-neutral-800')} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ArtFrame>
  );
};


// The real thing, in miniature: pick an answer and every option shows why it
// is or isn't right, then the explanation and the note it came from.
const SampleCard = () => {
  const [picked, setPicked] = useState(null);
  const answered = picked !== null;
  const gotIt = answered && SAMPLE_CARD.options.find((o) => o.id === picked).correct;

  return (
    <div className='rounded-lg border border-neutral-800 bg-neutral-900 p-6 shadow-sm sm:p-8'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-2'>
        <span className='font-mono text-xs uppercase tracking-[0.18em] text-primary-light'>{SAMPLE_CARD.course} · {SAMPLE_CARD.deck}</span>
        {answered ? (
          <span className={classNames('inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-body text-xs', gotIt ? 'bg-[rgba(74,222,128,0.10)] text-[#4ADE80]' : 'bg-[rgba(248,113,113,0.10)] text-[#F87171]')} role='status'>
            <Icon name={gotIt ? 'check' : 'x'} className='h-3.5 w-3.5' />
            {gotIt ? 'Correct' : 'Not quite'}
          </span>
        ) : (
          <span className='font-body text-xs text-neutral-500'>Pick one</span>
        )}
      </div>

      <h3 className='mb-6 font-heading text-2xl leading-snug text-neutral-50'>{SAMPLE_CARD.prompt}</h3>

      <div className='grid gap-3' role='group' aria-label='Answer options'>
        {SAMPLE_CARD.options.map((option) => {
          const isPicked = picked === option.id;

          return (
            <button
              key={option.id}
              type='button'
              disabled={answered}
              onClick={() => setPicked(option.id)}
              className={classNames(
                'flex min-h-[44px] w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-900',
                !answered && 'border-neutral-700 hover:border-primary-light/60 hover:bg-neutral-800',
                answered && option.correct && 'border-[#4ADE80]/40 bg-[rgba(74,222,128,0.10)]',
                answered && !option.correct && isPicked && 'border-[#F87171]/40 bg-[rgba(248,113,113,0.10)]',
                answered && !option.correct && !isPicked && 'border-neutral-800 opacity-70'
              )}
            >
              <span className={classNames(
                'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border font-mono text-xs',
                answered && option.correct ? 'border-[#4ADE80]/50 text-[#4ADE80]' :
                  answered && isPicked ? 'border-[#F87171]/50 text-[#F87171]' : 'border-neutral-700 text-neutral-400'
              )}>
                {answered && option.correct ? <Icon name='check' className='h-3.5 w-3.5' /> :
                  answered && isPicked ? <Icon name='x' className='h-3.5 w-3.5' /> : option.id.toUpperCase()}
              </span>
              <span className='flex flex-col gap-1'>
                <span className='font-body text-base text-neutral-200'>{option.text}</span>
                {answered ? <span className='font-body text-sm text-neutral-400'>{option.rationale}</span> : null}
              </span>
            </button>
          );
        })}
      </div>

      {answered ? (
        <div className='mt-6 border-t border-neutral-800 pt-6'>
          <p className='mb-4 font-body text-base leading-relaxed text-neutral-300'>{SAMPLE_CARD.explanation}</p>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <span className='inline-flex items-center gap-2 rounded-md bg-neutral-950 px-3 py-1.5 font-mono text-xs text-neutral-400'>
              <Icon name='book' className='h-3.5 w-3.5 text-primary-light' />
              {SAMPLE_CARD.citation}
            </span>
            <button
              type='button'
              onClick={() => setPicked(null)}
              className='inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 font-body text-sm text-neutral-400 transition-colors duration-150 hover:bg-neutral-800 hover:text-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light'
            >
              <Icon name='reset' />
              Try again
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};


const CourseFeature = ({ course, flip }) => {
  return (
    <article className='grid items-center gap-8 lg:grid-cols-12 lg:gap-16'>
      <div className={classNames('lg:col-span-5', flip && 'lg:order-2')}>
        <ArtFrame className='max-w-md'>
          {MOTIFS[course.art]}
        </ArtFrame>
      </div>
      <div className='lg:col-span-7'>
        <div className='mb-4 flex flex-wrap items-center gap-3'>
          <span className='font-mono text-xs uppercase tracking-[0.18em] text-primary-light'>{course.code}</span>
          <span className='font-mono text-xs tracking-widest text-neutral-500'>{course.issuer}</span>
          <span className='inline-flex items-center gap-1.5 rounded-full bg-[rgba(43,176,191,0.10)] px-2.5 py-0.5 font-body text-xs text-primary-light'>
            <span className='h-1.5 w-1.5 rounded-full bg-primary-light' />
            Live
          </span>
        </div>
        <h3 className='mb-3 font-heading text-3xl tracking-tight text-neutral-50 sm:text-4xl'>{course.name}</h3>
        <p className='mb-4 font-body text-xl leading-snug text-primary-light'>{course.tagline}</p>
        <p className='mb-6 max-w-[60ch] font-body text-base text-neutral-400'>{course.description}</p>
        <DiamondList items={course.topics} />
      </div>
    </article>
  );
};


const WorkbenchCard = ({ item }) => {
  const live = item.status === 'Live';

  return (
    <li className='group relative flex flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 p-6 transition duration-150 hover:-translate-y-0.5 hover:border-neutral-700'>
      {/* A teal rule that draws itself across the top on hover. */}
      <span aria-hidden='true' className='absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 bg-primary-light transition-transform duration-300 group-hover:scale-x-100' />
      <div className='mb-3 flex items-center justify-between gap-2'>
        <span className='font-mono text-xs uppercase tracking-[0.18em] text-neutral-500'>{item.code}</span>
        <span className={classNames(
          'rounded-full px-2.5 py-0.5 font-body text-xs',
          live ? 'bg-[rgba(43,176,191,0.10)] text-primary-light' : 'bg-neutral-800 text-neutral-400'
        )}>
          {item.status}
        </span>
      </div>
      <h4 className='mb-2 font-heading text-xl text-neutral-50'>{item.name}</h4>
      <p className='font-body text-sm leading-normal text-neutral-400'>{item.blurb}</p>
    </li>
  );
};


const ScribeCardsPage = () => {
  return (
    <div className='overflow-x-hidden'>
      {/* Hero */}
      <section className='relative overflow-hidden border-b border-neutral-800'>
        {/* Blueprint grid fading out from the top, plus one barely-there orb. */}
        <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#27272A_1px,transparent_1px),linear-gradient(to_bottom,#27272A_1px,transparent_1px)] bg-[size:48px_48px] opacity-40 [mask-image:radial-gradient(ellipse_at_50%_0%,black_30%,transparent_75%)]' />
        <div aria-hidden='true' className='pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-primary-light opacity-[0.05] blur-3xl' />

        <div className='relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-12 lg:px-8'>
          <div className='lg:col-span-7'>
            <Overline className='mb-4'>A Code Scribes Project · Launched October 2026</Overline>
            <h1 className='mb-6 font-heading text-5xl tracking-tight text-neutral-50 sm:text-6xl'>Scribe Cards</h1>
            <NibRule />
            <p className='mt-6 max-w-xl font-body text-xl leading-relaxed text-neutral-300'>
              Certification flashcards built from the study notes of engineers who took the exam and passed it.
            </p>
            <p className='mt-4 max-w-xl font-body text-base text-neutral-400'>
              Built so you actually understand the core terminology and concepts, instead of memorizing answers.
            </p>
            <div className='mt-8 flex flex-wrap gap-3'>
              <PrimaryLink href={APP_URL}>Start Studying Free</PrimaryLink>
              <OutlineLink href='#sample'>
                Try a Card
                <Icon name='arrowRight' />
              </OutlineLink>
            </div>
          </div>
          <div className='lg:col-span-5'>
            <CardStack />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section aria-label='Scribe Cards at a glance' className='border-b border-neutral-800 bg-neutral-900/40'>
        <dl className='mx-auto grid max-w-7xl grid-cols-2 gap-px px-4 sm:px-6 lg:grid-cols-4 lg:px-8'>
          {STATS.map(({ value, label }) => (
            <div key={label} className='flex flex-col gap-1 py-8 text-center'>
              <dt className='order-2 font-body text-xs uppercase tracking-widest text-neutral-500'>{label}</dt>
              <dd className='order-1 font-heading text-4xl text-neutral-50'>{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        {/* Why */}
        <section className='py-16 sm:py-24'>
          <SectionHead
            num='01'
            title='Why Scribe Cards'
            blurb='Scribe Cards started as the notes from eight months of studying for and passing certifications. Along the way, it became clear the usual prep material had a problem.'
          />
          <div className='grid gap-6 lg:grid-cols-2'>
            <div className='rounded-lg border border-neutral-800 bg-neutral-900 p-6 sm:p-8'>
              <Overline className='mb-3 !text-neutral-500'>The problem</Overline>
              <h3 className='mb-6 font-heading text-2xl text-neutral-50 sm:text-3xl'>{PROBLEM.heading}</h3>
              <DiamondList items={PROBLEM.points} />
            </div>
            <div className='rounded-lg border border-primary-light/40 bg-neutral-900 bg-[radial-gradient(ellipse_at_100%_0%,rgba(43,176,191,0.10),transparent_60%)] p-6 sm:p-8'>
              <Overline className='mb-3'>The fix</Overline>
              <h3 className='mb-6 font-heading text-2xl text-neutral-50 sm:text-3xl'>{FIX.heading}</h3>
              <DiamondList items={FIX.points} />
            </div>
          </div>
        </section>

        {/* Sample card */}
        <section id='sample' className='scroll-mt-24 border-t border-neutral-800 py-16 sm:py-24'>
          <SectionHead
            num='02'
            title='Try a Card'
            blurb='This is a real card from the CCAR-F course. Answer it and you get the rationale behind every option, an explanation, and the note it came from.'
          />
          <div className='grid items-start gap-12 lg:grid-cols-12'>
            <div className='lg:col-span-7'>
              <SampleCard />
            </div>
            <aside className='lg:col-span-5'>
              <h3 className='mb-6 font-heading text-2xl text-neutral-50'>What's on every card</h3>
              <DiamondList items={[
                'Single and multi-select questions that test concepts, not trivia',
                'A rationale for every option, wrong answers included',
                'A short explanation that ties the answer back to the bigger idea',
                'A citation to the exact note it came from, so you can read the reasoning',
                'A star, so anything you want to revisit shows up in review mode'
              ]} />
            </aside>
          </div>
        </section>

        {/* Courses */}
        <section className='border-t border-neutral-800 py-16 sm:py-24'>
          <SectionHead
            num='03'
            title='The Courses'
            blurb='Each course is a study guide for one exam: the topics it covers, the notes to read for them, and the flashcards that test them.'
          />
          <div className='grid gap-20'>
            {COURSES.map((course, i) => <CourseFeature key={course.code} course={course} flip={i % 2 === 1} />)}
          </div>

          <div className='mb-8 mt-24 flex items-center gap-4'>
            <h3 className='font-heading text-2xl text-neutral-50'>More on the Workbench</h3>
            <span aria-hidden='true' className='h-px flex-1 bg-neutral-800' />
          </div>
          <ul className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
            {WORKBENCH.map((item) => <WorkbenchCard key={item.name} item={item} />)}
          </ul>
        </section>

        {/* How it works */}
        <section className='border-t border-neutral-800 py-16 sm:py-24'>
          <SectionHead num='04' title='How It Works' />
          <ol className='grid gap-6 md:grid-cols-3'>
            {STEPS.map((step, i) => (
              <li key={step.title} className='relative border-l border-neutral-800 pl-6'>
                <span aria-hidden='true' className='absolute -left-[5px] top-2 h-2.5 w-2.5 rotate-45 border border-primary-light bg-neutral-950' />
                <span className='mb-2 block font-body text-xs uppercase tracking-widest text-primary-light'>Step {i + 1}</span>
                <h3 className='mb-2 font-heading text-2xl text-neutral-50'>{step.title}</h3>
                <p className='font-body text-base leading-relaxed text-neutral-400'>{step.body}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* CTA */}
        <section className='pb-24'>
          <div className='relative overflow-hidden rounded-lg border border-primary-light/40 bg-neutral-900 bg-[radial-gradient(ellipse_at_0%_0%,rgba(43,176,191,0.10),transparent_60%)] p-8 sm:p-12'>
            <div className='grid items-center gap-8 lg:grid-cols-12'>
              <div className='lg:col-span-8'>
                <Overline className='mb-3'>Free for now</Overline>
                <h2 className='mb-4 font-heading text-4xl tracking-tight text-neutral-50'>Know the material before exam day.</h2>
                <p className='max-w-2xl font-body text-lg text-neutral-400'>
                  Scribe Cards are free while we're getting started. Paid plans are on the way, and
                  we'll share how to claim a free 3-month trial before they arrive. Keep an eye on our socials.
                </p>
              </div>
              <div className='flex lg:col-span-4 lg:justify-end'>
                <PrimaryLink href={APP_URL}>Open Scribe Cards</PrimaryLink>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ScribeCardsPage;
